import * as React from 'react';

import type { CourseItem, CourseItemType as EventingCourseItemType } from '@coursera/event-pulse-types';
import { type EventData, createTrackerStore } from '@coursera/event-pulse/react';

import type { ContentSummary, ContentSummaryType } from 'bundles/learner-progress/types/ContentSummary';
import type { Item } from 'bundles/learner-progress/types/Item';

/**
 * tracking data for eventing v3 compatible with the click_button event
 */
type TrackingData = Omit<EventData<'click_button'>, 'button'>;

export const [CourseFrameTrackingDataProvider, useCourseFrameTrackingData] = createTrackerStore<TrackingData>();

export type Props = React.PropsWithChildren<{
  computedItem: Item;
}>;

type ComputedItemType = ContentSummary extends ContentSummaryType<infer U, any> ? U : never;

/**
 * Converts the `typeName` of the `computedItem` to the `type` in the `course_item` product schema.
 *
 * @see static/bundles/learner-progress/types/ContentSummary.ts for the list of `typeName` variations.
 * @see https://github.com/webedx-spark/eventing-schemas/blob/main/schemas/eventingv3/entities/products/course_item.json for the `course_item` product schema.
 */
const computedItemTypeToEventingCourseItemType = (typeName: ComputedItemType): EventingCourseItemType => {
  switch (typeName) {
    case 'lecture':
      return 'lecture';
    case 'discussionPrompt':
    case 'gradedDiscussionPrompt':
      return 'discussion_prompt';
    case 'supplement':
      return 'reading';
    case 'quiz':
    case 'exam':
      return 'quiz';
    case 'peer':
    case 'phasedPeer':
    case 'gradedPeer':
    case 'closedPeer':
    case 'splitPeerReviewItem':
      return 'peer_review';
    case 'gradedProgramming':
    case 'ungradedProgramming':
    case 'programming':
      return 'programming_assignment';
    case 'ungradedLti':
    case 'gradedLti':
      return 'lti_tool';
    case 'teammateReview':
      return 'teammate_review';
    case 'staffGraded':
    case 'ungradedAssignment':
      return 'staff_graded';
    case 'workspaceLauncher':
      return 'workspace_launcher';
    case 'placeholder':
      return 'place_holder';
    case 'dialogue':
      return 'dialogue';
    case 'ungradedLab':
      return 'ungraded_lab';
    case 'wiseFlow':
      return 'wise_flow';
    default:
      return 'unknown';
  }
};

export const computedItemToCourseItem = (computedItem: Item): CourseItem => {
  return {
    id: computedItem.id,
    name: computedItem.name,
    type: computedItemTypeToEventingCourseItemType(computedItem.contentSummary.typeName),
    isLocked: computedItem.itemLockedStatus !== undefined,
    lockedReasonCode: computedItem.itemLockedReasonCode,
    isGraded: computedItem.isGraded,
  };
};

export const CourseFrameEventingProvider = ({ computedItem, children }: Props) => {
  const contextValue: TrackingData = {
    product: {
      type: 'course',
      id: computedItem.courseId,
    },
    // By default, this describes the currently active item.
    // It should be overridden in case we need to track a different item (e.g. an item being clicked on the course outline).
    courseItem: computedItemToCourseItem(computedItem),
  };

  return <CourseFrameTrackingDataProvider value={contextValue}>{children}</CourseFrameTrackingDataProvider>;
};
